import { resolveNodeField, normalizeField, type NodeData } from '../../assistant/utils/conditionEvaluation';
import type { IntrospectedField } from './types';

const MAX_SAMPLE_NODES = 200;
const MAX_SAMPLE_VALUES = 5;

const SKIP_FIELDS = new Set([
  'id', 'type', 'x', 'y', 'w', 'h', 'width', 'height', 'zIndex',
  'fillColor', 'strokeColor', 'strokeWidth', 'locked', 'photoRef',
  'personId', 'managerId', 'positionId', 'positionCode', 'departmentCode',
  'customFields', 'reportsTo', 'validFrom', 'validTo', 'isFuture',
]);

interface IntrospectionResult {
  textFields: IntrospectedField[];
  numericFields: IntrospectedField[];
  dateFields: IntrospectedField[];
  allFields: IntrospectedField[];
}

export function introspectFields(nodes: NodeData[]): IntrospectionResult {
  const sample = nodes.length > MAX_SAMPLE_NODES ? nodes.slice(0, MAX_SAMPLE_NODES) : nodes;
  const totalCount = sample.length;
  if (totalCount === 0) return { textFields: [], numericFields: [], dateFields: [], allFields: [] };

  const fieldStats = new Map<string, { values: Map<string, number>; count: number }>();

  for (const node of sample) {
    const sources = [node.attrs, node.props];
    for (const source of sources) {
      if (!source) continue;
      for (const [rawKey, val] of Object.entries(source)) {
        if (SKIP_FIELDS.has(rawKey) || val == null || val === '') continue;
        const key = normalizeField(rawKey);
        if (SKIP_FIELDS.has(key)) continue;
        let stats = fieldStats.get(key);
        if (!stats) {
          stats = { values: new Map(), count: 0 };
          fieldStats.set(key, stats);
        }
        stats.count++;
        const strVal = String(val);
        stats.values.set(strVal, (stats.values.get(strVal) ?? 0) + 1);
      }
    }

    // Also check well-known resolved fields
    for (const field of ['name', 'title', 'department', 'location', 'email']) {
      if (fieldStats.has(field)) continue;
      const val = resolveNodeField(node, field);
      if (val == null || val === '') continue;
      let stats = fieldStats.get(field);
      if (!stats) {
        stats = { values: new Map(), count: 0 };
        fieldStats.set(field, stats);
      }
      stats.count++;
      const strVal = String(val);
      stats.values.set(strVal, (stats.values.get(strVal) ?? 0) + 1);
    }
  }

  const textFields: IntrospectedField[] = [];
  const numericFields: IntrospectedField[] = [];
  const dateFields: IntrospectedField[] = [];

  for (const [fieldId, stats] of fieldStats) {
    const coverage = stats.count / totalCount;
    const distinctValues = stats.values.size;
    const sampleValues = Array.from(stats.values.keys()).slice(0, MAX_SAMPLE_VALUES);
    const fieldType = classifyField(sampleValues);
    const label = toLabel(fieldId);

    const field: IntrospectedField = {
      id: fieldId,
      label,
      fieldType,
      distinctValues,
      coverage,
      sampleValues,
    };

    if (fieldType === 'number') numericFields.push(field);
    else if (fieldType === 'date') dateFields.push(field);
    else textFields.push(field);
  }

  return {
    textFields,
    numericFields,
    dateFields,
    allFields: [...textFields, ...numericFields, ...dateFields],
  };
}

function classifyField(sampleValues: string[]): 'text' | 'number' | 'date' {
  if (sampleValues.length === 0) return 'text';
  const allNumbers = sampleValues.every(v => !isNaN(Number(v)) && v.trim() !== '');
  if (allNumbers) return 'number';
  const datePattern = /^\d{4}-\d{2}-\d{2}/;
  const allDates = sampleValues.every(v => datePattern.test(v));
  if (allDates) return 'date';
  return 'text';
}

function toLabel(fieldId: string): string {
  const overrides: Record<string, string> = {
    flightRisk: 'Flight Risk',
    performance: 'Performance',
    potential: 'Potential',
    readiness: 'Readiness',
    salary: 'Salary',
    band: 'Pay Band',
    employmentType: 'Employment Type',
    costCenter: 'Cost Center',
    directReports: 'Direct Reports',
    startDate: 'Start Date',
    department: 'Department',
    location: 'Location',
    title: 'Job Title',
    name: 'Name',
  };
  if (overrides[fieldId]) return overrides[fieldId];
  return fieldId
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

export function getOrgFieldDisplayLabel(fieldId: string): string {
  return toLabel(fieldId);
}
